import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { POSITION_FILLED_WINDOW_EVENT } from '@/components/PositionFilledListener';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { isUpsellError } from '@/components/employer/MessagingUpgradeDialog';
import { DeclinedSubsDialog, UnavailabilityDialog, MessageDialog, ArchivedSchoolsDialog, ArchivedPositionsDialog, EditPositionDialog, PositionCard, PositionsCalendar, PositionDetailsDialog, PositionsFilterPanel, PositionsFilterTrigger } from '@/components/employer';
import PlanLimitTooltip from '@/components/PlanLimitTooltip';
import TrialBanner from '@/components/subscription/TrialBanner';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { schools, positions, notifications as notifApi, auth, messages } from '@/lib/api';
import { getStoredUser, clearStoredUser } from '@/lib/auth';
import { playNotificationSound } from '@/lib/audioSettings';
import { DEFAULT_POS_SORT_FIELD, DEFAULT_POS_SORT_DIR, countActiveSortFilter, getEffectiveSortFilter } from '@/lib/positionSortFilter';
import { 
  Clock, Plus, Building2, Users, Bell, Settings, LogOut, 
  CalendarIcon, CalendarDays, MapPin, User, ChevronRight, BarChart3,
  Copy, CheckCircle, XCircle, Loader2, RefreshCw, CreditCard, UserCog, Mail, Eye,
  HelpCircle, BookOpen, Send, MessageSquare, X, Archive, ArchiveRestore, Edit, AlertTriangle, Trash2, Shield, ChevronDown, ArrowUpDown, Filter, Tags, List, LayoutGrid, Search
} from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';
import AppFooter from '@/components/AppFooter';

export default function EmployerDashboard() {
  const navigate = useNavigate();
  const { isAtLimit, planHasFeature, upgradeTooltipMessage, refreshStatus } = usePlanLimits();
  const [user, setUser] = useState(getStoredUser());
  const [schoolsList, setSchoolsList] = useState([]);
  const [positionsList, setPositionsList] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  // Account-level subscription state (from billing_accounts, the source of
  // truth). The subscription belongs to the school-admin account, not a
  // school, so the sidebar "Billing & Plans" link points at the account-level
  // subscription page — it works even when the employer has no schools yet.
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateSchool, setShowCreateSchool] = useState(false);
  const [showCreatePosition, setShowCreatePosition] = useState(false);
  // Create-position submission guard. The ref is a synchronous lock (state
  // updates are async, so two clicks in the same frame could both pass a
  // state-only check); the state drives the disabled button + spinner UI.
  const [isSubmittingPosition, setIsSubmittingPosition] = useState(false);
  const isSubmittingPositionRef = useRef(false);
  const [createPositionError, setCreatePositionError] = useState('');
  // "No Subs Selected" confirmation shown before broadcasting to every Sub.
  const [showNoSubsConfirm, setShowNoSubsConfirm] = useState(false);
  const [schoolTOCs, setSchoolTOCs] = useState([]);
  // Post-position "Select Subs to Notify" category pre-filter (school-scoped)
  const [postCategoryFilter, setPostCategoryFilter] = useState([]);
  // Sub List tab controls
  const [subListSortField, setSubListSortField] = useState('name'); // 'name' | 'recent' | 'added'
  const [subListSortDir, setSubListSortDir] = useState('asc'); // 'asc' | 'desc'
  const [subListCategoryFilter, setSubListCategoryFilter] = useState([]);
  const [savingCategoriesFor, setSavingCategoriesFor] = useState(null);
  // Positions screen: view mode + sort + filters (preserved across view toggles & school switches)
  const [positionsView, setPositionsView] = useState('list'); // 'list' | 'calendar'
  const [posSortField, setPosSortField] = useState(DEFAULT_POS_SORT_FIELD); // 'posted' | 'position_date' | 'status' | 'subject'
  const [posSortDir, setPosSortDir] = useState(DEFAULT_POS_SORT_DIR); // 'asc' | 'desc'
  const [posStatusFilter, setPosStatusFilter] = useState([]); // ['open','filled','cancelled']
  const [posOfferedTo, setPosOfferedTo] = useState('all'); // toc user_id or 'all'
  const [posFilledBy, setPosFilledBy] = useState('all'); // toc user_id or 'all'
  const [posBeforeDate, setPosBeforeDate] = useState('');
  const [posAfterDate, setPosAfterDate] = useState('');
  const [posSearch, setPosSearch] = useState('');
  const [showSortFilterPanel, setShowSortFilterPanel] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [detailsPosition, setDetailsPosition] = useState(null);
  const [showPositionDetails, setShowPositionDetails] = useState(false);
  const [pendingTOCs, setPendingTOCs] = useState([]);
  const [showDeclinedDialog, setShowDeclinedDialog] = useState(false);
  const [declinedPosition, setDeclinedPosition] = useState(null);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showMessagingUpgrade, setShowMessagingUpgrade] = useState(false);
  const [selectedTocForMessage, setSelectedTocForMessage] = useState(null);
  const [selectedSchoolForMessage, setSelectedSchoolForMessage] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [showUnavailabilityDialog, setShowUnavailabilityDialog] = useState(false);
  const [selectedTocUnavailability, setSelectedTocUnavailability] = useState(null);
  const [loadingUnavailability, setLoadingUnavailability] = useState(false);
  const [archivedTOCs, setArchivedTOCs] = useState([]);
  const [isArchiving, setIsArchiving] = useState(false);
  
  // Archived positions state
  const [showArchivedPositions, setShowArchivedPositions] = useState(false);
  const [archivedPositionsList, setArchivedPositionsList] = useState([]);
  const [loadingArchivedPositions, setLoadingArchivedPositions] = useState(false);
  
  // Archived schools state
  const [showArchivedSchools, setShowArchivedSchools] = useState(false);
  const [archivedSchoolsList, setArchivedSchoolsList] = useState([]);
  
  // Edit position state
  const [showEditPosition, setShowEditPosition] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [editPositionForm, setEditPositionForm] = useState({
    dates: [],
    start_time: '',
    end_time: '',
    grade_level: '',
    description: '',
    target_tocs: [],
    reminder_frequency_minutes: 5,
    max_reminders: 5
  });
  const [isUpdatingPosition, setIsUpdatingPosition] = useState(false);
  
  // Sub bookings cache (for showing availability when creating positions)
  const [subBookings, setSubBookings] = useState({});
  
  // Track notification count for audio alerts
  const lastNotificationCount = useRef(-1);
  
  // Form states
  const [schoolForm, setSchoolForm] = useState({
    name: '',
    address: '',
    start_time: '08:00',
    end_time: '15:00',
    contact_person: '',
    contact_position: '',
    contact_email: '',
    contact_phone: '',
    grade_levels: []
  });
  const [newGradeLevel, setNewGradeLevel] = useState('');
  
  const [positionForm, setPositionForm] = useState({
    school_id: '',
    selectedDates: [],  // Start with no dates selected
    useTodayQuickSelect: false,  // Track if "Today" was clicked
    start_time: '08:00',
    end_time: '15:00',
    grade_level: '',
    description: '',
    target_tocs: [],
    reminder_frequency_minutes: 5,
    max_reminders: 5  // Default 5 reminders
  });
  
  const fetchData = useCallback(async () => {
    try {
      const [schoolsRes, positionsRes, notifsRes, bookingsRes, archivedSchoolsRes] = await Promise.all([
        schools.list(),
        positions.list(),
        notifApi.list(),
        positions.getAllAccepted().catch(() => ({ data: {} })),
        schools.getArchivedSchools().catch(() => ({ data: [] }))
      ]);
      
      setSchoolsList(schoolsRes.data);
      setPositionsList(positionsRes.data);
      setNotifications(notifsRes.data);
      setSubBookings(bookingsRes.data || {});
      setArchivedSchoolsList(archivedSchoolsRes.data || []);
      
      // Play sound for new notifications (messages, Sub responses, TOC requests, etc.)
      const unreadNotifications = notifsRes.data.filter(n => !n.read);
      const newNotificationCount = unreadNotifications.length;
      
      if (lastNotificationCount.current === -1) {
        // First fetch — just set baseline, don't play sound
        lastNotificationCount.current = newNotificationCount;
      } else if (newNotificationCount > lastNotificationCount.current) {
        // Find the newest unread notification
        const newestNotification = unreadNotifications.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        )[0];
        
        // Play sound for message notifications, Sub responses, and TOC requests
        if (newestNotification && (
          newestNotification.type === 'message' || 
          newestNotification.type === 'toc_response' ||
          newestNotification.type === 'toc_accepted' ||
          newestNotification.type === 'toc_declined' ||
          newestNotification.type === 'toc_request'
        )) {
          playNotificationSound();
          
          // Show toast for message notifications
          if (newestNotification.type === 'message') {
            toast.info('New message received!', {
              description: newestNotification.message?.substring(0, 100) || 'You have a new message',
              duration: 8000
            });
          }
          
          // Show toast for new TOC join requests
          if (newestNotification.type === 'toc_request') {
            toast.info('New Sub request!', {
              description: newestNotification.message?.substring(0, 100) || 'A substitute has requested to join your list',
              duration: 8000
            });
          }
        }
        lastNotificationCount.current = newNotificationCount;
      } else {
        lastNotificationCount.current = newNotificationCount;
      }
      
      // Determine which school to use for sub-data fetching
      const activeSchool = selectedSchool || (schoolsRes.data.length > 0 ? schoolsRes.data[0] : null);
      
      if (activeSchool) {
        if (!selectedSchool) {
          setSelectedSchool(activeSchool);
        }
        // Always refresh TOCs, pending TOCs, and archived TOCs on every poll
        const [tocsRes, pendingRes, archivedRes] = await Promise.all([
          schools.getTOCs(activeSchool.school_id),
          schools.getPendingTOCs(activeSchool.school_id).catch(() => ({ data: [] })),
          schools.getArchivedTOCs(activeSchool.school_id).catch(() => ({ data: [] }))
        ]);
        setSchoolTOCs(tocsRes.data);
        setPendingTOCs(pendingRes.data);
        setArchivedTOCs(archivedRes.data);
      }
    } catch (error) {
      // Silent fail - data will be retried on next poll
    } finally {
      setIsLoading(false);
    }
  }, [selectedSchool]);
  
  useEffect(() => {
    fetchData();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // KAN-156: refresh immediately when a real-time POSITION_FILLED event arrives.
  useEffect(() => {
    const onFilled = () => fetchData();
    window.addEventListener(POSITION_FILLED_WINDOW_EVENT, onFilled);
    return () => window.removeEventListener(POSITION_FILLED_WINDOW_EVENT, onFilled);
  }, [fetchData]);

  // Auto-send reminders every 90 seconds for open positions
  useEffect(() => {
    const sendAutoReminders = async () => {
      try {
        await positions.sendAllReminders();
      } catch (_e) {
        // Silent fail for auto reminders - will retry on next interval
      }
    };
    
    // Send reminders every 90 seconds
    const reminderInterval = setInterval(sendAutoReminders, 90000);
    
    // Also send on initial load after a delay
    const initialTimeout = setTimeout(sendAutoReminders, 5000);
    
    return () => {
      clearInterval(reminderInterval);
      clearTimeout(initialTimeout);
    };
  }, []);
  
  const handleSchoolSelect = async (school) => {
    setSelectedSchool(school);
    // Preserve view mode, status/date/text filters & sort across schools, but clear
    // the Sub-specific selectors since Sub ids are school-scoped (prevents confusion).
    setPosOfferedTo('all');
    setPosFilledBy('all');
    try {
      const [tocsRes, pendingRes, archivedRes] = await Promise.all([
        schools.getTOCs(school.school_id),
        schools.getPendingTOCs(school.school_id).catch(() => ({ data: [] })),
        schools.getArchivedTOCs(school.school_id).catch(() => ({ data: [] }))
      ]);
      setSchoolTOCs(tocsRes.data);
      setPendingTOCs(pendingRes.data);
      setArchivedTOCs(archivedRes.data);
    } catch (error) {
      // Silent fail - TOC list will be empty
    }
  };
  
  const handleCreateSchool = async (e) => {
    e.preventDefault();
    try {
      const response = await schools.create(schoolForm);
      refreshStatus(); // usage +1 school — keep isAtLimit('schools') in sync
      toast.success('School created successfully!');
      setSchoolsList(prev => [...prev, response.data]);
      setSelectedSchool(response.data);
      setShowCreateSchool(false);
      setSchoolForm({ 
        name: '', 
        address: '', 
        start_time: '08:00', 
        end_time: '15:00',
        contact_person: '',
        contact_position: '',
        contact_email: '',
        contact_phone: '',
        grade_levels: []
      });
      setNewGradeLevel('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create school');
    }
  };
  
  const handleAddGradeLevel = () => {
    if (!newGradeLevel.trim()) return;
    if (schoolForm.grade_levels.includes(newGradeLevel.trim())) {
      toast.error('This category already exists');
      return;
    }
    setSchoolForm(prev => ({
      ...prev,
      grade_levels: [...prev.grade_levels, newGradeLevel.trim()]
    }));
    setNewGradeLevel('');
  };
  
  const handleRemoveGradeLevel = (level) => {
    setSchoolForm(prev => ({
      ...prev,
      grade_levels: prev.grade_levels.filter(l => l !== level)
    }));
  };
  
  const handleCreatePosition = async (e) => {
    e?.preventDefault();
    // Guard: ignore repeat clicks / Enter presses while a post is in flight
    // or while the "No Subs Selected" confirmation is open.
    if (isSubmittingPositionRef.current || isSubmittingPosition || showNoSubsConfirm) return;
    
    if (!positionForm.school_id) {
      toast.error('Please select a school');
      return;
    }
    
    if (!positionForm.selectedDates || positionForm.selectedDates.length === 0) {
      toast.error('Please select at least one date');
      return;
    }
    
    // No specific Subs selected -> confirm before broadcasting to everyone.
    // No API call and no spinner until the admin explicitly chooses.
    if (!positionForm.target_tocs || positionForm.target_tocs.length === 0) {
      setCreatePositionError('');
      setShowNoSubsConfirm(true);
      return;
    }
    
    await submitPosition();
  };
  
  // "Post to All Subs" in the confirmation: close it and run the broadcast post.
  const handleConfirmPostToAllSubs = async () => {
    if (isSubmittingPositionRef.current) return;
    setShowNoSubsConfirm(false);
    await submitPosition();
  };
  
  // "Cancel": back to the form with every entered detail intact, no spinner.
  const handleCancelPostToAllSubs = () => {
    setShowNoSubsConfirm(false);
  };
  
  const submitPosition = async () => {
    if (isSubmittingPositionRef.current) return;
    isSubmittingPositionRef.current = true;
    setIsSubmittingPosition(true);
    setCreatePositionError('');
    try {
      // Convert Date objects to strings
      const dates = positionForm.selectedDates.map(d => format(d, 'yyyy-MM-dd'));
      
      const data = {
        school_id: positionForm.school_id,
        dates: dates,  // Array of date strings
        start_time: positionForm.start_time,
        end_time: positionForm.end_time,
        grade_level: positionForm.grade_level,
        description: positionForm.description,
        target_tocs: positionForm.target_tocs.length > 0 ? positionForm.target_tocs : [],
        reminder_frequency_minutes: positionForm.reminder_frequency_minutes,
        max_reminders: positionForm.max_reminders
      };
      
      await positions.create(data);
      refreshStatus(); // usage +1 posting this month — keep isAtLimit('postings') in sync
      toast.success('Position posted successfully!');
      setShowCreatePosition(false);
      fetchData();
      
      // Reset form but keep school defaults
      const school = schoolsList.find(s => s.school_id === positionForm.school_id);
      setPositionForm({
        school_id: positionForm.school_id,
        selectedDates: [],
        useTodayQuickSelect: false,
        start_time: school?.start_time || '08:00',
        end_time: school?.end_time || '15:00',
        grade_level: '',
        description: '',
        target_tocs: [],
        reminder_frequency_minutes: 5,
        max_reminders: null
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : (error.code === 'ECONNABORTED' || !error.response)
          ? 'Network error or timeout while posting. Please check your connection and try again.'
          : 'Failed to create position. Please try again.';
      setCreatePositionError(message);
    } finally {
      isSubmittingPositionRef.current = false;
      setIsSubmittingPosition(false);
    }
  };
  
  const handleApproveTOC = async (tocId, action) => {
    try {
      await schools.approveTOC(selectedSchool.school_id, tocId, action);
      refreshStatus(); // approve changes the active-Sub count — keep isAtLimit('subs') in sync
      toast.success(action === 'approve' ? 'Sub approved!' : 'Request declined');
      fetchData();
      // Refresh pending TOCs
      const pendingRes = await schools.getPendingTOCs(selectedSchool.school_id).catch(() => ({ data: [] }));
      setPendingTOCs(pendingRes.data);
      // Refresh TOC list
      const tocsRes = await schools.getTOCs(selectedSchool.school_id);
      setSchoolTOCs(tocsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    }
  };
  
  const handleCancelPositionDirect = async (positionId) => {
    try {
      await positions.cancel(positionId);
      toast.success('Position cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel position');
    }
  };
  
  const handleSendReminders = async (positionId) => {
    try {
      const response = await positions.sendReminders(positionId, true);
      if (response.data.reminders_sent > 0) {
        toast.success(`Reminders sent to ${response.data.reminders_sent} Sub(s)`);
      } else {
        toast.info('No Subs need reminders at this time');
      }
    } catch (error) {
      toast.error('Failed to send reminders');
    }
  };
  
  const handleViewUnavailability = async (toc) => {
    if (!selectedSchool) return;
    setLoadingUnavailability(true);
    try {
      const response = await schools.getTOCUnavailability(selectedSchool.school_id, toc.user_id);
      setSelectedTocUnavailability({
        ...response.data,
        toc_name: toc.name
      });
      setShowUnavailabilityDialog(true);
    } catch (error) {
      toast.error('Failed to load unavailability');
    } finally {
      setLoadingUnavailability(false);
    }
  };
  
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('School code copied to clipboard');
  };
  
  // Open message dialog and load conversation
  const openMessageDialog = async (schoolId, tocId, tocName) => {
    setSelectedSchoolForMessage(schoolId);
    setSelectedTocForMessage({ user_id: tocId, name: tocName });
    setShowMessageDialog(true);
    
    try {
      const response = await messages.list(schoolId, tocId);
      // Sort by date, newest first
      const sortedMessages = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setConversation(sortedMessages);
    } catch (error) {
      // Silent fail - empty conversation will be shown
      setConversation([]);
    }
  };
  
  // Poll for new messages when message dialog is open
  useEffect(() => {
    if (!showMessageDialog || !selectedSchoolForMessage || !selectedTocForMessage) return;
    
    const pollMessages = async () => {
      try {
        const response = await messages.list(selectedSchoolForMessage, selectedTocForMessage.user_id);
        const sortedMessages = response.data.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        setConversation(sortedMessages);
      } catch (_e) {
        // Silent fail
      }
    };
    
    // Poll every 5 seconds
    const interval = setInterval(pollMessages, 5000);
    
    return () => clearInterval(interval);
  }, [showMessageDialog, selectedSchoolForMessage, selectedTocForMessage]);
  
  // Send a message
  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedSchoolForMessage || !selectedTocForMessage) return;
    
    setIsSendingMessage(true);
    try {
      await messages.sendToTOC(selectedTocForMessage.user_id, selectedSchoolForMessage, messageText.trim());
      
      // Reload conversation
      const response = await messages.list(selectedSchoolForMessage, selectedTocForMessage.user_id);
      const sortedMessages = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setConversation(sortedMessages);
      setMessageText('');
      toast.success('Message sent');
    } catch (error) {
      if (isUpsellError(error)) {
        // Plan lacks messaging (Head Start): keep the draft, show upgrade prompt.
        setShowMessagingUpgrade(true);
      } else {
        toast.error('Failed to send message');
      }
    } finally {
      setIsSendingMessage(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await auth.logout();
    } catch (_e) {
      // Silent fail - proceed with local logout
    }
    clearStoredUser();
    navigate('/');
  };
  
  // Archive a Sub
  const handleArchiveTOC = async (tocId) => {
    if (!selectedSchool) return;
    setIsArchiving(true);
    try {
      await schools.archiveTOC(selectedSchool.school_id, tocId);
      refreshStatus(); // active-Sub count dropped — Approve may now be enabled
      // Move TOC from active to archived list
      const archivedToc = schoolTOCs.find(t => t.user_id === tocId);
      if (archivedToc) {
        setSchoolTOCs(prev => prev.filter(t => t.user_id !== tocId));
        setArchivedTOCs(prev => [...prev, { ...archivedToc, status: 'archived' }]);
      }
      toast.success('Sub archived');
    } catch (error) {
      toast.error('Failed to archive Sub');
    } finally {
      setIsArchiving(false);
    }
  };
  
  // Unarchive a Sub
  const handleUnarchiveTOC = async (tocId) => {
    if (!selectedSchool) return;
    setIsArchiving(true);
    try {
      await schools.unarchiveTOC(selectedSchool.school_id, tocId);
      refreshStatus(); // active-Sub count rose — Approve may now be disabled
      // Move TOC from archived back to active list
      const restoredToc = archivedTOCs.find(t => t.user_id === tocId);
      if (restoredToc) {
        setArchivedTOCs(prev => prev.filter(t => t.user_id !== tocId));
        setSchoolTOCs(prev => [...prev, { ...restoredToc, status: 'active' }]);
      }
      toast.success('Sub restored to active list');
    } catch (error) {
      toast.error('Failed to restore Sub');
    } finally {
      setIsArchiving(false);
    }
  };
  
  // Edit Position handlers
  const handleOpenEditPosition = (position) => {
    setEditingPosition(position);
    setEditPositionForm({
      dates: position.dates || [position.date],
      start_time: position.start_time || '08:00',
      end_time: position.end_time || '15:00',
      grade_level: position.grade_level || '',
      description: position.description || '',
      target_tocs: position.target_tocs || [],
      reminder_frequency_minutes: position.reminder_frequency_minutes || 5,
      max_reminders: position.max_reminders === undefined ? null : position.max_reminders
    });
    setShowEditPosition(true);
  };
  
  const handleUpdatePosition = async () => {
    if (!editingPosition) return;
    setIsUpdatingPosition(true);
    try {
      await positions.update(editingPosition.position_id, {
        dates: editPositionForm.dates,
        start_time: editPositionForm.start_time,
        end_time: editPositionForm.end_time,
        grade_level: editPositionForm.grade_level,
        description: editPositionForm.description,
        target_tocs: editPositionForm.target_tocs,
        reminder_frequency_minutes: editPositionForm.reminder_frequency_minutes,
        max_reminders: editPositionForm.max_reminders
      });
      toast.success('Position updated successfully');
      setShowEditPosition(false);
      setEditingPosition(null);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error('Failed to update position');
    } finally {
      setIsUpdatingPosition(false);
    }
  };
  
  const handleCancelPosition = async () => {
    if (!editingPosition) return;
    setIsUpdatingPosition(true);
    try {
      await positions.update(editingPosition.position_id, {
        status: 'cancelled'
      });
      toast.success('Position cancelled');
      setShowEditPosition(false);
      setEditingPosition(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel position');
    } finally {
      setIsUpdatingPosition(false);
    }
  };
  
  // Archive a filled position
  const handleArchivePosition = async (positionId) => {
    try {
      await positions.archive(positionId);
      toast.success('Position archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to archive position');
    }
  };
  
  // Load archived positions
  const handleOpenArchivedPositions = async () => {
    setLoadingArchivedPositions(true);
    setShowArchivedPositions(true);
    try {
      const res = await positions.listArchived(
        selectedSchool ? { school_id: selectedSchool.school_id } : {}
      );
      // Sort by first date descending (most recent first)
      const sorted = (res.data || []).sort((a, b) => {
        const dateA = (a.dates && a.dates[0]) || a.date || '';
        const dateB = (b.dates && b.dates[0]) || b.date || '';
        return dateB.localeCompare(dateA);
      });
      setArchivedPositionsList(sorted);
    } catch (error) {
      toast.error('Failed to load archived positions');
    } finally {
      setLoadingArchivedPositions(false);
    }
  };
  
  // Permanently delete an archived position
  const handlePermanentDeletePosition = async (positionId) => {
    try {
      await positions.permanentDelete(positionId);
      toast.success('Position permanently deleted');
      setArchivedPositionsList(prev => prev.filter(p => p.position_id !== positionId));
    } catch (error) {
      toast.error('Failed to delete position');
    }
  };
  
  // Archived schools handlers
  const handleOpenArchivedSchools = async () => {
    setShowArchivedSchools(true);
    try {
      const res = await schools.getArchivedSchools();
      setArchivedSchoolsList(res.data || []);
    } catch (error) {
      toast.error('Failed to load archived schools');
    }
  };
  
  const handleRestoreSchool = async (schoolId) => {
    try {
      await schools.unarchiveSchool(schoolId);
      refreshStatus(); // active-school count rose — keep isAtLimit('schools') in sync
      toast.success('School restored');
      setArchivedSchoolsList(prev => prev.filter(s => s.school_id !== schoolId));
      fetchData(); // Refresh main school list
    } catch (error) {
      toast.error('Failed to restore school');
    }
  };
  
  // Check if a sub is booked on specific dates
  const isSubBookedOnDates = (subId, dates) => {
    const bookings = subBookings[subId] || [];
    return dates.some(date => bookings.some(b => b.date === date));
  };
  
  // Get booking info for a sub on specific dates
  const getSubBookingInfo = (subId, dates) => {
    const bookings = subBookings[subId] || [];
    const conflicting = bookings.filter(b => dates.includes(b.date));
    if (conflicting.length === 0) return null;
    const schoolNames = [...new Set(conflicting.map(b => b.school_name))];
    return schoolNames.join(', ');
  };

  // Determine if a Sub is fully unavailable for ALL currently selected dates.
  // These are the Subs rendered with strikethrough / amber styling and are
  // excluded from the bulk "Select All" action.
  const isTocFullyUnavailable = (toc) => {
    const selectedDateStrs = positionForm.selectedDates.map(d => format(d, 'yyyy-MM-dd'));
    if (selectedDateStrs.length === 0) return false;
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    const isUnavailableToday = toc.declined_all_today === todayStr && selectedDateStrs.includes(todayStr);
    const unavailableDates = toc.declined_dates?.filter(d => selectedDateStrs.includes(d)) || [];
    const isUnavailable = isUnavailableToday || unavailableDates.length > 0;
    return isUnavailable && unavailableDates.length === selectedDateStrs.length;
  };

  // IDs of Subs eligible for bulk "Select All" (exclude blocked + fully unavailable Subs).
  const getSelectableTocIds = () => schoolTOCs
    .filter(t => t.status !== 'blocked' && !isTocFullyUnavailable(t))
    .map(t => t.user_id);

  // Sub Teacher Categories configured for the currently selected/posting school
  const activeSubCategories = useMemo(
    () => selectedSchool?.sub_teacher_categories || [],
    [selectedSchool]
  );

  // Post-position picker list filtered by the school-scoped category pre-filter.
  // A Sub matches if it holds AT LEAST ONE of the selected categories (at this school).
  const postFilteredTOCs = useMemo(() => {
    if (!postCategoryFilter.length) return schoolTOCs;
    return schoolTOCs.filter(t =>
      (t.sub_categories || []).some(c => postCategoryFilter.includes(c))
    );
  }, [schoolTOCs, postCategoryFilter]);

  // Helper: rank for "most recent position" sorting.
  // Later work dates rank higher (future above past). "None" (null) sorts last.
  const recentRank = (toc) => toc.most_recent_position || '';

  // Sub List tab: filtered + sorted list of Subs.
  const displayedSubs = useMemo(() => {
    let list = [...schoolTOCs];
    // Multi-select category filter (match at least one)
    if (subListCategoryFilter.length) {
      list = list.filter(t =>
        (t.sub_categories || []).some(c => subListCategoryFilter.includes(c))
      );
    }
    const dir = subListSortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      let cmp = 0;
      if (subListSortField === 'name') {
        const an = (a.last_name || a.name || '').toLowerCase();
        const bn = (b.last_name || b.name || '').toLowerCase();
        cmp = an.localeCompare(bn);
      } else if (subListSortField === 'recent') {
        const ar = recentRank(a);
        const br = recentRank(b);
        // "None" always sorts last regardless of direction
        if (!ar && !br) cmp = 0;
        else if (!ar) return 1;
        else if (!br) return -1;
        else cmp = ar < br ? -1 : (ar > br ? 1 : 0);
      } else if (subListSortField === 'added') {
        const ad = a.joined_at || '';
        const bd = b.joined_at || '';
        cmp = ad < bd ? -1 : (ad > bd ? 1 : 0);
      }
      return cmp * dir;
    });
    return list;
  }, [schoolTOCs, subListCategoryFilter, subListSortField, subListSortDir]);

  // Toggle a Sub Teacher Category for a Sub at the active school (persist immediately).
  const handleToggleSubCategory = async (tocId, category) => {
    const toc = schoolTOCs.find(t => t.user_id === tocId);
    if (!toc || !selectedSchool) return;
    const current = toc.sub_categories || [];
    const next = current.includes(category)
      ? current.filter(c => c !== category)
      : [...current, category];
    // Optimistic update
    setSchoolTOCs(prev => prev.map(t => t.user_id === tocId ? { ...t, sub_categories: next } : t));
    setSavingCategoriesFor(tocId);
    try {
      await schools.updateTOCCategories(selectedSchool.school_id, tocId, next);
    } catch (e) {
      // Revert on failure
      setSchoolTOCs(prev => prev.map(t => t.user_id === tocId ? { ...t, sub_categories: current } : t));
      toast.error('Failed to update categories');
    } finally {
      setSavingCategoriesFor(null);
    }
  };

  const formatRecentDate = (dateStr) => {
    if (!dateStr) return 'None';
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy');
    } catch (_e) {
      return dateStr;
    }
  };
  
  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);
  
  // Stats for selected school
  const schoolPositions = useMemo(() => positionsList.filter(p => p.school_id === selectedSchool?.school_id), [positionsList, selectedSchool]);
  const openPositions = useMemo(() => schoolPositions.filter(p => p.status === 'open').length, [schoolPositions]);
  const filledPositions = useMemo(() => schoolPositions.filter(p => p.status === 'filled').length, [schoolPositions]);
  const todayPositions = useMemo(() => schoolPositions.filter(p => p.date === format(new Date(), 'yyyy-MM-dd')).length, [schoolPositions]);
  
  // ---- Positions screen: filtering + sorting ----
  // Earliest work date for a position (first work date); null when missing.
  const positionFirstDate = (p) => {
    const ds = (p.dates && p.dates.length) ? p.dates : [p.start_date || p.date].filter(Boolean);
    if (!ds.length) return null;
    return [...ds].sort()[0];
  };

  // Sort/filter is a paid-plan feature. Without it the list uses the defaults and any sort/filter
  // state left over from a previous plan or school is ignored (search is not gated).
  const canSortFilter = planHasFeature(selectedSchool?.plan_id, 'position_sort_filter');
  // Collapse the panel when entitlement is lost so it cannot reappear expanded after switching back.
  useEffect(() => {
    if (!canSortFilter) setShowSortFilterPanel(false);
  }, [canSortFilter]);
  const effective = useMemo(() => getEffectiveSortFilter(canSortFilter, {
    sortField: posSortField,
    sortDir: posSortDir,
    statusFilter: posStatusFilter,
    offeredTo: posOfferedTo,
    filledBy: posFilledBy,
    afterDate: posAfterDate,
    beforeDate: posBeforeDate,
  }), [canSortFilter, posSortField, posSortDir, posStatusFilter, posOfferedTo, posFilledBy, posAfterDate, posBeforeDate]);

  // Sort/filter panel settings only (the search box lives outside the panel).
  const activeSortFilterCount = useMemo(() => countActiveSortFilter(effective), [effective]);

  // Drives the empty-state "Reset Filters" buttons, so it includes the search box (sort excluded).
  const hasActivePosFilters = useMemo(() => (
    effective.statusFilter.length > 0 ||
    effective.offeredTo !== 'all' ||
    effective.filledBy !== 'all' ||
    !!effective.beforeDate ||
    !!effective.afterDate ||
    posSearch.trim() !== ''
  ), [effective, posSearch]);

  const resetPosFilters = () => {
    setPosStatusFilter([]);
    setPosOfferedTo('all');
    setPosFilledBy('all');
    setPosBeforeDate('');
    setPosAfterDate('');
    setPosSearch('');
  };

  // The panel's Reset also restores the default sort so the trigger badge returns to zero.
  const resetPanelFilters = () => {
    setPosStatusFilter([]);
    setPosOfferedTo('all');
    setPosFilledBy('all');
    setPosBeforeDate('');
    setPosAfterDate('');
    setPosSortField(DEFAULT_POS_SORT_FIELD);
    setPosSortDir(DEFAULT_POS_SORT_DIR);
  };

  // Filters applied to BOTH list & calendar views (logical AND across controls).
  const filteredPositions = useMemo(() => {
    const term = posSearch.trim().toLowerCase();
    return schoolPositions.filter((p) => {
      // Status (OR within control). 'cancelled' option also covers archived.
      if (effective.statusFilter.length > 0) {
        const isCancelledOrArchived = p.status === 'cancelled' || p.archived === true;
        const match = effective.statusFilter.some((s) => {
          if (s === 'cancelled') return isCancelledOrArchived;
          return p.status === s;
        });
        if (!match) return false;
      }
      // Positions offered to a specific Sub (target list)
      if (effective.offeredTo !== 'all') {
        if (!(p.target_tocs || []).includes(effective.offeredTo)) return false;
      }
      // Positions filled by a specific Sub
      if (effective.filledBy !== 'all') {
        if (p.accepted_by !== effective.filledBy) return false;
      }
      // Date range (on the position's work dates)
      const firstDate = positionFirstDate(p);
      if (effective.afterDate) {
        const ds = (p.dates && p.dates.length) ? p.dates : [p.start_date || p.date].filter(Boolean);
        if (!ds.some((d) => d >= effective.afterDate)) return false;
      }
      if (effective.beforeDate) {
        const ds = (p.dates && p.dates.length) ? p.dates : [p.start_date || p.date].filter(Boolean);
        if (!ds.some((d) => d <= effective.beforeDate)) return false;
      }
      // Global text search across fields
      if (term) {
        const sub = schoolTOCs.find((t) => t.user_id === p.accepted_by);
        const haystack = [
          p.grade_level,
          p.description,
          p.accepted_by_name,
          sub?.name,
          selectedSchool?.name,
          selectedSchool?.address,
          firstDate,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [schoolPositions, effective, posSearch, schoolTOCs, selectedSchool]);

  // Sorting applies to List View only. Null date fields sort last regardless of direction.
  const sortedPositions = useMemo(() => {
    const dir = effective.sortDir === 'asc' ? 1 : -1;
    const statusRank = (p) => {
      // Group by fulfillment: Unfilled/Open first, then Filled, then Cancelled.
      if (p.status === 'open') return 0;
      if (p.status === 'filled') return 1;
      return 2;
    };
    const list = [...filteredPositions];
    list.sort((a, b) => {
      let cmp = 0;
      if (effective.sortField === 'posted') {
        const ac = a.created_at || '';
        const bc = b.created_at || '';
        cmp = ac < bc ? -1 : (ac > bc ? 1 : 0);
      } else if (effective.sortField === 'position_date') {
        const ad = positionFirstDate(a);
        const bd = positionFirstDate(b);
        if (!ad && !bd) cmp = 0;
        else if (!ad) return 1;   // nulls last
        else if (!bd) return -1;  // nulls last
        else cmp = ad < bd ? -1 : (ad > bd ? 1 : 0);
      } else if (effective.sortField === 'status') {
        cmp = statusRank(a) - statusRank(b);
      } else if (effective.sortField === 'subject') {
        const asu = (a.grade_level || '').toLowerCase();
        const bsu = (b.grade_level || '').toLowerCase();
        cmp = asu.localeCompare(bsu);
      }
      return cmp * dir;
    });
    return list;
  }, [filteredPositions, effective]);

  const openPositionDetails = (position) => {
    setDetailsPosition(position);
    setShowPositionDetails(true);
  };

  // KAN-156: deep link from the position-filled toast: /employer?position=<id>&school=<id>
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkPositionId = searchParams.get('position');
  const deepLinkSchoolId = searchParams.get('school');
  useEffect(() => {
    if (!deepLinkPositionId || isLoading) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await positions.get(deepLinkPositionId);
        if (cancelled) return;
        const targetSchoolId = deepLinkSchoolId || res.data?.school_id;
        const school = schoolsList.find(sch => sch.school_id === targetSchoolId);
        if (school && school.school_id !== selectedSchool?.school_id) setSelectedSchool(school);
        openPositionDetails(res.data);
      } catch (_e) {
        if (!cancelled) toast.error('That position is no longer available');
      } finally {
        if (!cancelled) setSearchParams({}, { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkPositionId, deepLinkSchoolId, isLoading]);
  
  // Sidebar sections rendered for every employer — including accounts with no
  // schools yet — so a brand-new school admin can reach Billing & Plans (and the
  // pricing page) before creating their first school. Shared between the
  // empty-state layout and the populated dashboard layout.
  const sidebarAccountSection = (
    <>
      {/* Billing & Plans Sidebar Section (owner only — hidden while a
          co-managed school is selected; co-admins cannot manage billing) */}
      {selectedSchool?.viewer_role !== 'co_admin' && (
        <div className="mt-6 pt-6 border-t border-slate-200">
          <h2 className="text-sm font-semibold text-slate-500 mb-3">Account</h2>
          <div className="space-y-2">
            {/* Account-level target: the subscription belongs to the school
                admin account, so everyone goes to the account management page
                — it renders the right action for every state (Subscribe for
                free plans, Upgrade/Downgrade for paid ones) and works with
                zero schools. */}
            <Link to="/employer/subscription">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-slate-600 hover:text-slate-900"
                data-testid="sidebar-billing-plans-link"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Billing & Plans
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Help Links Sidebar Section */}
      <div className="mt-6 pt-6 border-t border-slate-200">
        <h2 className="text-sm font-semibold text-slate-500 mb-3">Help & Resources</h2>
        <div className="space-y-2">
          <Link to="/guides/admin">
            <Button variant="ghost" size="sm" className="w-full justify-start text-slate-600 hover:text-slate-900" data-testid="sidebar-user-guide-link">
              <BookOpen className="h-4 w-4 mr-2" />
              User Guide
            </Button>
          </Link>
          <Link to="/onboarding/admin">
            <Button variant="ghost" size="sm" className="w-full justify-start text-slate-600 hover:text-slate-900" data-testid="sidebar-onboarding-link">
              <HelpCircle className="h-4 w-4 mr-2" />
              Getting Started
            </Button>
          </Link>
        </div>
      </div>
    </>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandLogo variant="white" size="lg" />
            <Badge variant="secondary" className="ml-2 bg-slate-700 text-slate-300">Employer</Badge>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Help Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-white hover:bg-slate-800"
                  data-testid="help-menu-btn"
                >
                  <HelpCircle className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/guides/admin" className="flex items-center gap-2 cursor-pointer">
                    <BookOpen className="h-4 w-4" />
                    User Guide
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/onboarding/admin" className="flex items-center gap-2 cursor-pointer">
                    <HelpCircle className="h-4 w-4" />
                    Getting Started
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Popover open={showNotifications} onOpenChange={setShowNotifications}>
              <PopoverTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="relative text-white hover:bg-slate-800"
                  data-testid="notifications-btn"
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="p-3 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-slate-900">Notifications</h3>
                    {unreadCount > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-xs h-7"
                        onClick={async () => {
                          await notifApi.markAllRead();
                          fetchData();
                        }}
                      >
                        Mark all read
                      </Button>
                    )}
                  </div>
                </div>
                <ScrollArea className="h-80">
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center text-slate-500">
                      <Bell className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">No notifications yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {notifications.slice(0, 20).map((notif) => (
                        <div 
                          key={notif.notification_id}
                          className={`p-3 hover:bg-slate-50 cursor-pointer ${!notif.read ? 'bg-slate-50' : ''}`}
                          onClick={async () => {
                            if (!notif.read) {
                              await notifApi.markRead(notif.notification_id);
                              fetchData();
                            }
                            
                            // If it's a message notification, open message dialog directly
                            if (notif.type === 'new_message' && notif.school_id && notif.sender_id) {
                              // Find sender name from the message or school TOCs
                              const school = schoolsList.find(s => s.school_id === notif.school_id);
                              if (school) {
                                // Try to get sender info from school's TOC list
                                try {
                                  const tocsRes = await schools.getTOCs(notif.school_id);
                                  const sender = tocsRes.data.find(t => t.user_id === notif.sender_id);
                                  openMessageDialog(notif.school_id, notif.sender_id, sender?.name || 'Substitute');
                                } catch (e) {
                                  // Fallback: just open with sender_id
                                  openMessageDialog(notif.school_id, notif.sender_id, 'Substitute');
                                }
                              }
                            }
                            setShowNotifications(false);
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              (() => {
                                if (notif.type === 'new_message') return 'bg-blue-100';
                                if (notif.type === 'position_accepted') return 'bg-emerald-100';
                                if (notif.type === 'toc_connected') return 'bg-purple-100';
                                return 'bg-slate-100';
                              })()
                            }`}>
                              {(() => {
                                if (notif.type === 'new_message') return <Mail className="h-4 w-4 text-blue-600" />;
                                if (notif.type === 'position_accepted') return <CheckCircle className="h-4 w-4 text-emerald-600" />;
                                if (notif.type === 'toc_connected') return <User className="h-4 w-4 text-purple-600" />;
                                return <Bell className="h-4 w-4 text-slate-600" />;
                              })()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notif.read ? 'font-medium text-slate-900' : 'text-slate-600'}`}>
                                {notif.message}
                              </p>
                              <p className="text-xs text-slate-400 mt-1">
                                {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                              </p>
                            </div>
                            {!notif.read && (
                              <div className="h-2 w-2 rounded-full bg-slate-900 flex-shrink-0 mt-1.5" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>
            
            <div className="flex items-center gap-1 sm:gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-slate-400">{user?.email}</p>
              </div>
              {/* Profile & Logout - Visible on larger screens */}
              <div className="hidden sm:flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="text-white hover:bg-slate-800"
                  onClick={() => navigate('/profile')}
                  data-testid="profile-btn"
                >
                  <User className="h-5 w-5" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="text-white hover:bg-slate-800"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut className="h-5 w-5" />
                </Button>
              </div>
              {/* Mobile User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="text-white hover:bg-slate-800 sm:hidden"
                    data-testid="mobile-user-menu-btn"
                  >
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5 border-b border-slate-100">
                    <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                    <User className="h-4 w-4 mr-2" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={handleLogout} 
                    className="cursor-pointer text-red-600 focus:text-red-600"
                    data-testid="mobile-logout-btn"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <TrialBanner />
        {schoolsList.length === 0 ? (
          /* Empty State - No Schools */
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Sidebar - persistent nav even with no schools */}
            <div className="lg:col-span-3 space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h2 className="text-lg font-semibold text-slate-900 font-['Manrope']">Your Schools</h2>
                <p className="text-sm text-slate-500 mt-1">
                  No schools yet. Create your first school to get started.
                </p>
              </div>
              {sidebarAccountSection}
            </div>

            {/* Main area - create-school CTA */}
            <div className="lg:col-span-9">
              <div className="flex flex-col items-center justify-center py-20">
                <div className="h-20 w-20 rounded-full bg-slate-100 flex items-center justify-center mb-6">
                  <Building2 className="h-10 w-10 text-slate-400" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 font-['Manrope'] mb-2">
                  Welcome to ExpressSub!
                </h2>
                <p className="text-slate-600 mb-8 text-center max-w-md">
                  Get started by creating your first school. You'll get a unique code to share with your substitute teachers.
                </p>
                <Dialog open={showCreateSchool} onOpenChange={setShowCreateSchool}>
                  <PlanLimitTooltip
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="create-first-school-btn"
                    disabled={isAtLimit('schools')}
                    onClick={() => setShowCreateSchool(true)}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create Your First School
                  </PlanLimitTooltip>
                  <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-['Manrope']">Create School</DialogTitle>
                      <DialogDescription>
                        Set up your school's default information for position postings.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateSchool} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="school-name">School Name *</Label>
                        <Input
                          id="school-name"
                          value={schoolForm.name}
                          onChange={(e) => setSchoolForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Lincoln Elementary"
                          required
                          data-testid="school-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="school-address">Address *</Label>
                        <Input
                          id="school-address"
                          value={schoolForm.address}
                          onChange={(e) => setSchoolForm(prev => ({ ...prev, address: e.target.value }))}
                          placeholder="123 School St, City, State"
                          required
                          data-testid="school-address-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="school-start-time">Default Start Time *</Label>
                          <Input
                            id="school-start-time"
                            type="time"
                            value={schoolForm.start_time}
                            onChange={(e) => setSchoolForm(prev => ({ ...prev, start_time: e.target.value }))}
                            required
                            data-testid="school-start-time-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="school-end-time">Default End Time *</Label>
                          <Input
                            id="school-end-time"
                            type="time"
                            value={schoolForm.end_time}
                            onChange={(e) => setSchoolForm(prev => ({ ...prev, end_time: e.target.value }))}
                            required
                            data-testid="school-end-time-input"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 -mt-2">
                        These times will be pre-filled when you create new positions
                      </p>
                      <div className="space-y-2">
                        <Label htmlFor="school-contact">Contact Person *</Label>
                        <Input
                          id="school-contact"
                          value={schoolForm.contact_person}
                          onChange={(e) => setSchoolForm(prev => ({ ...prev, contact_person: e.target.value }))}
                          placeholder="John Smith"
                          required
                          data-testid="school-contact-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="school-contact-position">Contact Position/Title</Label>
                        <Input
                          id="school-contact-position"
                          value={schoolForm.contact_position}
                          onChange={(e) => setSchoolForm(prev => ({ ...prev, contact_position: e.target.value }))}
                          placeholder="e.g., Principal, Office Manager"
                          data-testid="school-contact-position-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="school-contact-email">Contact Email</Label>
                          <Input
                            id="school-contact-email"
                            type="email"
                            value={schoolForm.contact_email}
                            onChange={(e) => setSchoolForm(prev => ({ ...prev, contact_email: e.target.value }))}
                            placeholder="contact@school.edu"
                            data-testid="school-contact-email-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="school-contact-phone">Contact Phone</Label>
                          <Input
                            id="school-contact-phone"
                            type="tel"
                            value={schoolForm.contact_phone}
                            onChange={(e) => setSchoolForm(prev => ({ ...prev, contact_phone: e.target.value }))}
                            placeholder="(555) 123-4567"
                            data-testid="school-contact-phone-input"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 -mt-2">
                        Contact information is visible to approved Subs
                      </p>
                  
                      {/* Categories Section */}
                      <div className="space-y-2 pt-2 border-t border-slate-200">
                        <Label>Position Categories</Label>
                        <p className="text-xs text-slate-500">
                          Define the categories of positions available at your school. These appear when posting positions.
                        </p>
                        <div className="flex gap-2">
                          <Input
                            value={newGradeLevel}
                            onChange={(e) => setNewGradeLevel(e.target.value)}
                            placeholder="e.g., Kindergarten, Grade 1"
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddGradeLevel())}
                            data-testid="new-grade-level-input"
                          />
                          <Button 
                            type="button"
                            onClick={handleAddGradeLevel}
                            variant="outline"
                            size="icon"
                            data-testid="add-grade-level-btn"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        {schoolForm.grade_levels.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {schoolForm.grade_levels.map((level) => (
                              <Badge 
                                key={level} 
                                variant="secondary"
                                className="flex items-center gap-1 px-2 py-1"
                              >
                                {level}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveGradeLevel(level)}
                                  className="ml-1 hover:text-red-600"
                                  data-testid={`remove-grade-${level}`}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        )}
                        {schoolForm.grade_levels.length === 0 && (
                          <p className="text-xs text-slate-400 italic">
                            No categories defined yet. Default options will be shown when posting.
                          </p>
                        )}
                      </div>
                  
                      <DialogFooter className="pt-4">
                        <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="submit-school-btn">
                          Create School
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
        ) : (
          /* Main Dashboard */
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Sidebar - Schools List */}
            <div className="lg:col-span-3 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900 font-['Manrope']">Your Schools</h2>
                <Dialog open={showCreateSchool} onOpenChange={setShowCreateSchool}>
                  <PlanLimitTooltip
                    size="sm"
                    variant="outline"
                    data-testid="add-school-btn"
                    disabled={isAtLimit('schools')}
                    onClick={() => setShowCreateSchool(true)}
                  >
                    <Plus className="h-4 w-4" />
                  </PlanLimitTooltip>
                  <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-['Manrope']">Create School</DialogTitle>
                      <DialogDescription>
                        Set up your school's default information for position postings.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateSchool} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="school-name-2">School Name *</Label>
                        <Input
                          id="school-name-2"
                          value={schoolForm.name}
                          onChange={(e) => setSchoolForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Lincoln Elementary"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="school-address-2">Address *</Label>
                        <Input
                          id="school-address-2"
                          value={schoolForm.address}
                          onChange={(e) => setSchoolForm(prev => ({ ...prev, address: e.target.value }))}
                          placeholder="123 School St, City, State"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="school-start-time-2">Default Start Time *</Label>
                          <Input
                            id="school-start-time-2"
                            type="time"
                            value={schoolForm.start_time}
                            onChange={(e) => setSchoolForm(prev => ({ ...prev, start_time: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="school-end-time-2">Default End Time *</Label>
                          <Input
                            id="school-end-time-2"
                            type="time"
                            value={schoolForm.end_time}
                            onChange={(e) => setSchoolForm(prev => ({ ...prev, end_time: e.target.value }))}
                            required
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 -mt-2">
                        These times will be pre-filled when you create new positions
                      </p>
                      <div className="space-y-2">
                        <Label htmlFor="school-contact-2">Contact Person *</Label>
                        <Input
                          id="school-contact-2"
                          value={schoolForm.contact_person}
                          onChange={(e) => setSchoolForm(prev => ({ ...prev, contact_person: e.target.value }))}
                          placeholder="John Smith"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="school-contact-position-2">Contact Position/Title</Label>
                        <Input
                          id="school-contact-position-2"
                          value={schoolForm.contact_position}
                          onChange={(e) => setSchoolForm(prev => ({ ...prev, contact_position: e.target.value }))}
                          placeholder="e.g., Principal, Office Manager"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="school-contact-email-2">Contact Email</Label>
                          <Input
                            id="school-contact-email-2"
                            type="email"
                            value={schoolForm.contact_email}
                            onChange={(e) => setSchoolForm(prev => ({ ...prev, contact_email: e.target.value }))}
                            placeholder="contact@school.edu"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="school-contact-phone-2">Contact Phone</Label>
                          <Input
                            id="school-contact-phone-2"
                            type="tel"
                            value={schoolForm.contact_phone}
                            onChange={(e) => setSchoolForm(prev => ({ ...prev, contact_phone: e.target.value }))}
                            placeholder="(555) 123-4567"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 -mt-2">
                        Contact information is visible to approved Subs
                      </p>
                      
                      {/* Categories Section */}
                      <div className="space-y-2 pt-2 border-t border-slate-200">
                        <Label>Position Categories</Label>
                        <p className="text-xs text-slate-500">
                          Define the categories of positions available at your school. These appear when posting positions.
                        </p>
                        <div className="flex gap-2">
                          <Input
                            value={newGradeLevel}
                            onChange={(e) => setNewGradeLevel(e.target.value)}
                            placeholder="e.g., Kindergarten, Grade 1"
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddGradeLevel())}
                          />
                          <Button 
                            type="button"
                            onClick={handleAddGradeLevel}
                            variant="outline"
                            size="icon"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        {schoolForm.grade_levels.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {schoolForm.grade_levels.map((level) => (
                              <Badge 
                                key={level} 
                                variant="secondary"
                                className="flex items-center gap-1 px-2 py-1"
                              >
                                {level}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveGradeLevel(level)}
                                  className="ml-1 hover:text-red-600"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        )}
                        {schoolForm.grade_levels.length === 0 && (
                          <p className="text-xs text-slate-400 italic">
                            No categories defined yet. Default options will be shown when posting.
                          </p>
                        )}
                      </div>
                      
                      <DialogFooter className="pt-4">
                        <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                          Create School
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              
              <div className="space-y-2">
                {schoolsList.map((school) => (
                  <Card 
                    key={school.school_id}
                    className={`cursor-pointer transition-all ${
                      selectedSchool?.school_id === school.school_id 
                        ? 'border-slate-900 bg-slate-50' 
                        : 'hover:border-slate-300'
                    }`}
                    onClick={() => handleSchoolSelect(school)}
                    data-testid={`school-card-${school.school_id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-slate-900">{school.name}</h3>
                          {school.viewer_role === 'co_admin' && (
                            <Badge className="mt-1 bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid="co-admin-school-badge">
                              <Shield className="h-3 w-3 mr-1" /> Co-Admin
                            </Badge>
                          )}
                          <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                            <Users className="h-3 w-3" />
                            {school.toc_list?.length || 0} Connected Subs
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-slate-400" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {/* Archived Schools Button - only shows if there are archived schools */}
              {archivedSchoolsList.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-slate-500 hover:text-slate-700 mt-2"
                  onClick={handleOpenArchivedSchools}
                  data-testid="archived-schools-btn"
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Archived Schools ({archivedSchoolsList.length})
                </Button>
              )}

              {/* Account (Billing & Plans — owner-only) + Help & Resources */}
              {sidebarAccountSection}
            </div>
            
            {/* Main Content Area */}
            <div className="lg:col-span-9 space-y-6">
              {selectedSchool && (
                <>
                  {/* School Header */}
                  <Card className="border-slate-200">
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
                            {selectedSchool.name}
                          </h1>
                          <p className="text-slate-500 flex items-center gap-1 mt-1">
                            <MapPin className="h-4 w-4" />
                            {selectedSchool.address}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="bg-slate-100 rounded-lg px-4 py-2">
                            <p className="text-xs text-slate-500 mb-1">School Code</p>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-slate-900">
                                {selectedSchool.school_code}
                              </span>
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                className="h-6 w-6"
                                onClick={() => handleCopyCode(selectedSchool.school_code)}
                                data-testid="copy-school-code-btn"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <Link to={`/employer/school/${selectedSchool.school_id}/tocs`}>
                            <Button variant="outline" size="icon" data-testid="manage-tocs-btn" title="Manage Subs">
                              <UserCog className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Link to={`/employer/school/${selectedSchool.school_id}`}>
                            <Button variant="outline" size="icon" data-testid="school-settings-btn" title="Settings">
                              <Settings className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Link to={`/employer/analytics/${selectedSchool.school_id}`}>
                            <Button variant="outline" size="icon" data-testid="school-analytics-btn" title="Analytics">
                              <BarChart3 className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Link to={`/employer/school/${selectedSchool.school_id}/subscription`}>
                            <Button variant="outline" size="icon" data-testid="subscription-btn" title="Subscription">
                              <CreditCard className="h-4 w-4" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="border-slate-200">
                      <CardContent className="p-4">
                        <p className="text-sm text-slate-500">Today's Positions</p>
                        <p className="text-2xl font-bold text-slate-900">{todayPositions}</p>
                      </CardContent>
                    </Card>
                    <Card className="border-slate-200">
                      <CardContent className="p-4">
                        <p className="text-sm text-slate-500">Open</p>
                        <p className="text-2xl font-bold text-amber-600">{openPositions}</p>
                      </CardContent>
                    </Card>
                    <Card className="border-slate-200">
                      <CardContent className="p-4">
                        <p className="text-sm text-slate-500">Filled</p>
                        <p className="text-2xl font-bold text-emerald-600">{filledPositions}</p>
                      </CardContent>
                    </Card>
                    <Card className="border-slate-200">
                      <CardContent className="p-4">
                        <p className="text-sm text-slate-500">Connected Subs</p>
                        <p className="text-2xl font-bold text-slate-900">{schoolTOCs.length}</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Pending Requests Alert */}
                  {pendingTOCs.length > 0 && (
                    <Card className="border-amber-300 bg-amber-50">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="bg-amber-100 rounded-full p-2">
                              <Users className="h-5 w-5 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-amber-900">
                                {pendingTOCs.length} Pending Request{pendingTOCs.length !== 1 ? 's' : ''}
                              </p>
                              <p className="text-sm text-amber-700">
                                Substitutes waiting to join your list
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 space-y-2">
                          {pendingTOCs.map((toc) => (
                            <div key={toc.user_id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-200">
                              <div className="flex items-center gap-3">
                                {toc.picture ? (
                                  <img src={toc.picture} alt="" className="h-8 w-8 rounded-full" />
                                ) : (
                                  <div className="h-8 w-8 bg-slate-200 rounded-full flex items-center justify-center">
                                    <User className="h-4 w-4 text-slate-500" />
                                  </div>
                                )}
                                <div>
                                  <p className="font-medium text-slate-900">{toc.name}</p>
                                  <p className="text-xs text-slate-500">{toc.email}</p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                                  onClick={() => handleApproveTOC(toc.user_id, 'reject')}
                                  data-testid={`reject-toc-${toc.user_id}`}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Decline
                                </Button>
                                <PlanLimitTooltip
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  onClick={() => handleApproveTOC(toc.user_id, 'approve')}
                                  data-testid={`approve-toc-${toc.user_id}`}
                                  disabled={isAtLimit('subs')}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Approve
                                </PlanLimitTooltip>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Positions & Subs Tabs */}
                  <Tabs defaultValue="positions" className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <TabsList>
                        <TabsTrigger value="positions" data-testid="positions-tab">Positions</TabsTrigger>
                        <TabsTrigger value="tocs" data-testid="tocs-tab">Sub List</TabsTrigger>
                        <TabsTrigger value="archived" data-testid="archived-tab">
                          Archived Subs
                          {archivedTOCs.length > 0 && (
                            <Badge variant="secondary" className="ml-1 text-xs">{archivedTOCs.length}</Badge>
                          )}
                        </TabsTrigger>
                      </TabsList>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleOpenArchivedPositions}
                          data-testid="view-archived-positions-btn"
                        >
                          <Archive className="h-4 w-4 mr-1" />
                          Archived Positions
                        </Button>
                        
                        <Dialog
                          open={showCreatePosition}
                          onOpenChange={(open) => {
                            // Keep the modal open while a post is in flight so the result (or error) is always shown.
                            if (!open && isSubmittingPositionRef.current) return;
                            if (!open) setShowNoSubsConfirm(false);
                            setShowCreatePosition(open);
                          }}
                        >
                        <PlanLimitTooltip
                          className="bg-emerald-600 hover:bg-emerald-700"
                          disabled={isAtLimit('postings')}
                          onClick={() => {
                            setPositionForm(prev => ({
                              ...prev,
                              school_id: selectedSchool.school_id,
                              start_time: selectedSchool.start_time,
                              end_time: selectedSchool.end_time || '15:00'
                            }));
                            setCreatePositionError('');
                            setShowCreatePosition(true);
                            setPostCategoryFilter([]);
                          }}
                          data-testid="post-position-btn"
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Post Position
                        </PlanLimitTooltip>
                        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="font-['Manrope']">Post New Position</DialogTitle>
                            <DialogDescription>
                              Create a position for {selectedSchool?.name}
                            </DialogDescription>
                          </DialogHeader>
                          <form onSubmit={handleCreatePosition} className="space-y-4">
                            {/* Date Selection with "Today" quick select */}
                            <div className="space-y-2">
                              <Label>Select Date(s)</Label>
                              
                              {/* Today Quick Select Button */}
                              <div className="flex items-center gap-2 mb-2">
                                <Button
                                  type="button"
                                  variant={positionForm.useTodayQuickSelect ? "default" : "outline"}
                                  size="sm"
                                  className={positionForm.useTodayQuickSelect ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                                  onClick={() => {
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    if (positionForm.useTodayQuickSelect) {
                                      // Deselect today
                                      setPositionForm(prev => ({
                                        ...prev,
                                        useTodayQuickSelect: false,
                                        selectedDates: prev.selectedDates.filter(d => 
                                          format(d, 'yyyy-MM-dd') !== format(today, 'yyyy-MM-dd')
                                        )
                                      }));
                                    } else {
                                      // Select today
                                      setPositionForm(prev => ({
                                        ...prev,
                                        useTodayQuickSelect: true,
                                        selectedDates: [today, ...prev.selectedDates.filter(d => 
                                          format(d, 'yyyy-MM-dd') !== format(today, 'yyyy-MM-dd')
                                        )]
                                      }));
                                    }
                                  }}
                                  data-testid="today-quick-select-btn"
                                >
                                  <CalendarDays className="h-4 w-4 mr-1" />
                                  Today
                                </Button>
                                <span className="text-xs text-slate-500">
                                  or select dates below
                                </span>
                              </div>
                              
                              <p className="text-xs text-slate-500 mb-2">
                                Click dates to add them (Sub must accept all or none)
                              </p>
                              <style>{`
                                .position-calendar .rdp-day_selected {
                                  background-color: #10b981 !important;
                                  color: white !important;
                                  font-weight: 500;
                                }
                                .position-calendar .rdp-day_selected:hover {
                                  background-color: #059669 !important;
                                }
                                .position-calendar [aria-selected="true"] {
                                  background-color: #10b981 !important;
                                  color: white !important;
                                  font-weight: 500;
                                }
                              `}</style>
                              <div className="border rounded-lg p-2 position-calendar">
                                <Calendar
                                  mode="multiple"
                                  selected={positionForm.selectedDates}
                                  onSelect={(dates) => {
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    const todayStr = format(today, 'yyyy-MM-dd');
                                    const isTodayInDates = dates?.some(d => format(d, 'yyyy-MM-dd') === todayStr);
                                    setPositionForm(prev => ({ 
                                      ...prev, 
                                      selectedDates: dates || [],
                                      useTodayQuickSelect: isTodayInDates
                                    }));
                                  }}
                                  disabled={(date) => {
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    return date < today;
                                  }}
                                  className="rounded-md"
                                  classNames={{
                                    day_selected: "bg-emerald-500 text-white hover:bg-emerald-600 focus:bg-emerald-500"
                                  }}
                                  data-testid="position-dates-calendar"
                                />
                              </div>
                              {positionForm.selectedDates.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {positionForm.selectedDates
                                    .sort((a, b) => a - b)
                                    .map((date) => (
                                    <Badge key={date.toISOString()} variant="secondary" className="text-xs">
                                      {format(date, 'MMM d')}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              <p className="text-xs text-emerald-600 mt-1">
                                {positionForm.selectedDates.length} date{positionForm.selectedDates.length !== 1 ? 's' : ''} selected
                              </p>
                            </div>
                            
                            <div className="space-y-2">
                              <Label htmlFor="grade-level">Position Category</Label>
                              <Select 
                                value={positionForm.grade_level}
                                onValueChange={(value) => setPositionForm(prev => ({ ...prev, grade_level: value }))}
                              >
                                <SelectTrigger data-testid="grade-level-select">
                                  <SelectValue placeholder="Select position category" />
                                </SelectTrigger>
                                <SelectContent>
                                  {/* Use school's custom categories if defined */}
                                  {selectedSchool?.grade_levels?.length > 0 ? (
                                    <>
                                      {selectedSchool.grade_levels.map((level) => (
                                        <SelectItem key={level} value={level.toLowerCase().replace(/\s+/g, '-')}>
                                          {level}
                                        </SelectItem>
                                      ))}
                                      <SelectItem value="multilevel">Multilevel</SelectItem>
                                    </>
                                  ) : (
                                    <>
                                      <SelectItem value="kindergarten">Kindergarten</SelectItem>
                                      <SelectItem value="grade-1">Grade 1</SelectItem>
                                      <SelectItem value="grade-2">Grade 2</SelectItem>
                                      <SelectItem value="grade-3">Grade 3</SelectItem>
                                      <SelectItem value="grade-4">Grade 4</SelectItem>
                                      <SelectItem value="grade-5">Grade 5</SelectItem>
                                      <SelectItem value="grade-6">Grade 6</SelectItem>
                                      <SelectItem value="grade-7">Grade 7</SelectItem>
                                      <SelectItem value="grade-8">Grade 8</SelectItem>
                                      <SelectItem value="grade-9">Grade 9</SelectItem>
                                      <SelectItem value="grade-10">Grade 10</SelectItem>
                                      <SelectItem value="grade-11">Grade 11</SelectItem>
                                      <SelectItem value="grade-12">Grade 12</SelectItem>
                                      <SelectItem value="multilevel">Multilevel</SelectItem>
                                    </>
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label htmlFor="start-time">Start Time</Label>
                                <Input
                                  id="start-time"
                                  type="time"
                                  value={positionForm.start_time}
                                  onChange={(e) => setPositionForm(prev => ({ ...prev, start_time: e.target.value }))}
                                  required
                                  data-testid="position-start-time"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="end-time">End Time</Label>
                                <Input
                                  id="end-time"
                                  type="time"
                                  value={positionForm.end_time}
                                  onChange={(e) => setPositionForm(prev => ({ ...prev, end_time: e.target.value }))}
                                  required
                                  data-testid="position-end-time"
                                />
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              <Label htmlFor="description">Description (Optional)</Label>
                              <Textarea
                                id="description"
                                value={positionForm.description}
                                onChange={(e) => setPositionForm(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Any additional notes about the position..."
                                data-testid="position-description"
                              />
                            </div>
                            
                            {/* Reminder Settings */}
                            <div className="space-y-3 p-4 bg-slate-50 rounded-lg border">
                              <Label className="text-sm font-semibold flex items-center gap-2">
                                <Bell className="h-4 w-4" />
                                Automatic Reminder Settings
                              </Label>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label htmlFor="reminder-frequency" className="text-xs text-slate-600">
                                    Reminder Frequency
                                  </Label>
                                  <Select
                                    value={String(positionForm.reminder_frequency_minutes)}
                                    onValueChange={(value) => setPositionForm(prev => ({ 
                                      ...prev, 
                                      reminder_frequency_minutes: parseInt(value) 
                                    }))}
                                  >
                                    <SelectTrigger id="reminder-frequency" data-testid="reminder-frequency">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="1">Every 1 minute</SelectItem>
                                      <SelectItem value="2">Every 2 minutes</SelectItem>
                                      <SelectItem value="5">Every 5 minutes</SelectItem>
                                      <SelectItem value="10">Every 10 minutes</SelectItem>
                                      <SelectItem value="15">Every 15 minutes</SelectItem>
                                      <SelectItem value="30">Every 30 minutes</SelectItem>
                                      <SelectItem value="60">Every 1 hour</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="max-reminders" className="text-xs text-slate-600">
                                    Stop After (# of reminders)
                                  </Label>
                                  <Select
                                    value={positionForm.max_reminders === null ? 'unlimited' : String(positionForm.max_reminders)}
                                    onValueChange={(value) => setPositionForm(prev => ({ 
                                      ...prev, 
                                      max_reminders: value === 'unlimited' ? null : parseInt(value) 
                                    }))}
                                  >
                                    <SelectTrigger id="max-reminders" data-testid="max-reminders">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="unlimited">Unlimited</SelectItem>
                                      <SelectItem value="3">3 reminders</SelectItem>
                                      <SelectItem value="5">5 reminders</SelectItem>
                                      <SelectItem value="10">10 reminders</SelectItem>
                                      <SelectItem value="15">15 reminders</SelectItem>
                                      <SelectItem value="20">20 reminders</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <p className="text-xs text-slate-500">
                                Reminders are sent automatically for open positions to Subs who haven't responded yet.
                              </p>
                            </div>
                            
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <Label>Select Subs to Notify</Label>
                                {schoolTOCs.length > 0 && (() => {
                                  const selectableIds = getSelectableTocIds();
                                  const allSelectableSelected = selectableIds.length > 0 && selectableIds.every(id => positionForm.target_tocs.includes(id));
                                  const anySelected = positionForm.target_tocs.length > 0;
                                  const checkedState = allSelectableSelected ? true : (anySelected ? 'indeterminate' : false);
                                  return (
                                    <div className="flex items-center space-x-2">
                                      <Checkbox
                                        id="select-all-tocs"
                                        checked={checkedState}
                                        onCheckedChange={() => {
                                          setPositionForm(prev => {
                                            const allSelectedNow = selectableIds.length > 0 && selectableIds.every(id => prev.target_tocs.includes(id));
                                            // Toggle off: deselect ALL currently selected (incl. manual overrides)
                                            if (allSelectedNow || selectableIds.length === 0) {
                                              return { ...prev, target_tocs: [] };
                                            }
                                            // Toggle on: select all AVAILABLE Subs while preserving any
                                            // manually-checked unavailable Subs (union).
                                            return {
                                              ...prev,
                                              target_tocs: Array.from(new Set([...prev.target_tocs, ...selectableIds]))
                                            };
                                          });
                                        }}
                                        data-testid="select-all-tocs-checkbox"
                                      />
                                      <Label htmlFor="select-all-tocs" className="text-xs cursor-pointer">
                                        Select All
                                      </Label>
                                    </div>
                                  );
                                })()}
                              </div>
                              <p className="text-xs text-slate-500 mb-2">
                                Choose specific Subs or select none to notify all active Subs
                              </p>
                              {activeSubCategories.length > 0 && (
                                <div className="mb-2">
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="text-xs w-full justify-between"
                                        data-testid="post-category-filter-trigger"
                                      >
                                        <span>
                                          {postCategoryFilter.length === 0
                                            ? 'Filter by Sub Teacher Category'
                                            : `${postCategoryFilter.length} categor${postCategoryFilter.length === 1 ? 'y' : 'ies'} selected`}
                                        </span>
                                        <ChevronDown className="h-3 w-3 opacity-60" />
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-64 p-2" align="start">
                                      <div className="max-h-56 overflow-y-auto space-y-1">
                                        {activeSubCategories.map((cat) => (
                                          <label
                                            key={cat}
                                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm"
                                          >
                                            <Checkbox
                                              checked={postCategoryFilter.includes(cat)}
                                              onCheckedChange={(checked) => {
                                                setPostCategoryFilter(prev =>
                                                  checked ? [...prev, cat] : prev.filter(c => c !== cat)
                                                );
                                              }}
                                              data-testid={`post-category-filter-${cat}`}
                                            />
                                            <span>{cat}</span>
                                          </label>
                                        ))}
                                      </div>
                                      {postCategoryFilter.length > 0 && (
                                        <button
                                          type="button"
                                          className="mt-2 w-full text-xs text-slate-500 hover:text-slate-700 text-center"
                                          onClick={() => setPostCategoryFilter([])}
                                          data-testid="post-category-filter-clear"
                                        >
                                          Clear filter
                                        </button>
                                      )}
                                    </PopoverContent>
                                  </Popover>
                                </div>
                              )}
                              <div className="max-h-96 overflow-y-auto space-y-2 border rounded-lg p-3 bg-slate-50">
                                {schoolTOCs.length === 0 ? (
                                  <p className="text-sm text-slate-500 text-center py-4">
                                    No Subs connected yet. Share your school code to build your list!
                                  </p>
                                ) : postFilteredTOCs.length === 0 ? (
                                  <p className="text-sm text-slate-500 text-center py-4" data-testid="post-no-category-match">
                                    No Subs match the selected categories.
                                  </p>
                                ) : (
                                  postFilteredTOCs.map((toc) => {
                                    // Check if TOC is unavailable for any of the selected dates
                                    const selectedDateStrs = positionForm.selectedDates.map(d => format(d, 'yyyy-MM-dd'));
                                    const todayStr = format(new Date(), 'yyyy-MM-dd');
                                    
                                    const isUnavailableToday = toc.declined_all_today === todayStr && selectedDateStrs.includes(todayStr);
                                    const unavailableDates = toc.declined_dates?.filter(d => selectedDateStrs.includes(d)) || [];
                                    const isUnavailable = isUnavailableToday || unavailableDates.length > 0;
                                    const allDatesUnavailable = isUnavailable && unavailableDates.length === selectedDateStrs.length;
                                    
                                    // Check if sub is already booked on any of the selected dates
                                    const isBooked = selectedDateStrs.length > 0 && isSubBookedOnDates(toc.user_id, selectedDateStrs);
                                    const bookingInfo = isBooked ? getSubBookingInfo(toc.user_id, selectedDateStrs) : null;
                                    
                                    return (
                                      <div 
                                        key={toc.user_id} 
                                        className={`flex items-center space-x-2 p-2 rounded ${
                                          toc.status === 'blocked' ? 'bg-rose-50 opacity-60' : 
                                          isBooked ? 'bg-blue-50' :
                                          allDatesUnavailable ? 'bg-amber-50' : 'bg-white'
                                        }`}
                                      >
                                        <Checkbox
                                          id={`toc-${toc.user_id}`}
                                          checked={positionForm.target_tocs.includes(toc.user_id)}
                                          disabled={toc.status === 'blocked'}
                                          onCheckedChange={(checked) => {
                                            setPositionForm(prev => ({
                                              ...prev,
                                              target_tocs: checked
                                                ? [...prev.target_tocs, toc.user_id]
                                                : prev.target_tocs.filter(id => id !== toc.user_id)
                                            }));
                                          }}
                                          data-testid={`toc-checkbox-${toc.user_id}`}
                                        />
                                        <Label 
                                          htmlFor={`toc-${toc.user_id}`} 
                                          className={`text-sm cursor-pointer flex-1 ${
                                            toc.status === 'blocked' ? 'text-slate-400' : 
                                            isBooked ? 'text-blue-600' :
                                            allDatesUnavailable ? 'line-through text-slate-400' : ''
                                          }`}
                                        >
                                          {toc.name}
                                          <span className="text-slate-400 ml-1">({toc.email})</span>
                                        </Label>
                                        {toc.status === 'blocked' && (
                                          <span className="text-xs text-rose-500 font-medium">Blocked</span>
                                        )}
                                        {isBooked && toc.status !== 'blocked' && (
                                          <span className="text-xs text-blue-600 font-medium" title={`Booked at: ${bookingInfo}`}>
                                            Booked
                                          </span>
                                        )}
                                        {isUnavailable && !isBooked && toc.status !== 'blocked' && (
                                          <span className="text-xs text-amber-600 font-medium">
                                            {allDatesUnavailable ? 'Unavailable' : `Unavailable ${unavailableDates.length}/${selectedDateStrs.length} days`}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                              {positionForm.target_tocs.length > 0 && (
                                <p className="text-xs text-emerald-600 mt-1">
                                  {positionForm.target_tocs.length} Sub{positionForm.target_tocs.length !== 1 ? 's' : ''} selected
                                </p>
                              )}
                            </div>
                            
                            {createPositionError && (
                              <Alert variant="destructive" className="border-red-200 bg-red-50" data-testid="create-position-error">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription className="text-red-700">
                                  {createPositionError}
                                </AlertDescription>
                              </Alert>
                            )}
                            
                            <DialogFooter>
                              <Button
                                type="submit"
                                className="bg-emerald-600 hover:bg-emerald-700 disabled:pointer-events-none disabled:opacity-70"
                                disabled={isSubmittingPosition}
                                aria-busy={isSubmittingPosition}
                                data-testid="submit-position-btn"
                              >
                                {isSubmittingPosition ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Posting...
                                  </>
                                ) : (
                                  'Post Position'
                                )}
                              </Button>
                            </DialogFooter>
                          </form>
                        </DialogContent>
                      </Dialog>
                      
                      {/* Confirm broadcasting to every Sub when none were selected */}
                      <Dialog
                        open={showNoSubsConfirm}
                        onOpenChange={(open) => { if (!open) handleCancelPostToAllSubs(); }}
                      >
                        {/* Regular Dialog (not AlertDialog) so Esc, outside click and Cancel all dismiss it */}
                        <DialogContent className="w-[calc(100%-2rem)] rounded-xl sm:max-w-md" data-testid="no-subs-confirm-dialog">
                          <DialogHeader>
                            <div className="mx-auto sm:mx-0 h-11 w-11 rounded-full bg-amber-100 flex items-center justify-center mb-1">
                              <Users className="h-5 w-5 text-amber-700" />
                            </div>
                            <DialogTitle className="font-['Manrope']">No Subs Selected</DialogTitle>
                            <DialogDescription data-testid="no-subs-confirm-message">
                              {"You haven't selected any specific subs for this position. Would you like to post this position to all available subs on your school list, or cancel to select specific subs?"}
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter className="gap-2 sm:gap-0">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={handleCancelPostToAllSubs}
                              data-testid="no-subs-confirm-cancel-btn"
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              onClick={handleConfirmPostToAllSubs}
                              disabled={isSubmittingPosition}
                              className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid="no-subs-confirm-post-all-btn"
                            >
                              Post to All Subs
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                    </div>
                    
                    <TabsContent value="positions" className="space-y-4">
                      {schoolPositions.length === 0 ? (
                        <Card className="border-slate-200">
                          <CardContent className="p-12 text-center">
                            <CalendarIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-slate-700 mb-2">No positions yet</h3>
                            <p className="text-sm text-slate-500">Post your first position to get started</p>
                          </CardContent>
                        </Card>
                      ) : (
                        <>
                          {/* ---- Positions Control Bar ---- */}
                          <Collapsible open={showSortFilterPanel} onOpenChange={setShowSortFilterPanel} className="bg-white border border-slate-200 rounded-lg p-3" data-testid="positions-control-bar">
                            {/* View toggle + global search (+ Sort & Filter expand/collapse trigger on paid plans) */}
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
                                <button
                                  type="button"
                                  onClick={() => setPositionsView('list')}
                                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${positionsView === 'list' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                                  data-testid="view-toggle-list"
                                >
                                  <List className="h-4 w-4" /> List
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setPositionsView('calendar')}
                                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${positionsView === 'calendar' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                                  data-testid="view-toggle-calendar"
                                >
                                  <LayoutGrid className="h-4 w-4" /> Calendar
                                </button>
                              </div>
                              <div className="relative flex-1 min-w-[180px]">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <Input
                                  value={posSearch}
                                  onChange={(e) => setPosSearch(e.target.value)}
                                  placeholder="Search positions..."
                                  className="h-9 pl-8 text-sm"
                                  data-testid="positions-search-input"
                                />
                              </div>
                              {canSortFilter && (
                                <PositionsFilterTrigger
                                  expanded={showSortFilterPanel}
                                  activeCount={activeSortFilterCount}
                                />
                              )}
                            </div>

                            {/* Expands inside this same panel (paid plans only) */}
                            {canSortFilter && (
                              <PositionsFilterPanel
                                positionsView={positionsView}
                                schoolTOCs={schoolTOCs}
                                sortField={posSortField}
                                onSortFieldChange={setPosSortField}
                                sortDir={posSortDir}
                                onSortDirChange={setPosSortDir}
                                statusFilter={posStatusFilter}
                                onStatusFilterChange={setPosStatusFilter}
                                offeredTo={posOfferedTo}
                                onOfferedToChange={setPosOfferedTo}
                                filledBy={posFilledBy}
                                onFilledByChange={setPosFilledBy}
                                afterDate={posAfterDate}
                                onAfterDateChange={setPosAfterDate}
                                beforeDate={posBeforeDate}
                                onBeforeDateChange={setPosBeforeDate}
                                hasActiveFilters={activeSortFilterCount > 0}
                                onReset={resetPanelFilters}
                              />
                            )}
                          </Collapsible>

                          {/* ---- Views ---- */}
                          {positionsView === 'calendar' ? (
                            <PositionsCalendar
                              positions={filteredPositions}
                              month={calendarMonth}
                              onMonthChange={setCalendarMonth}
                              onBadgeClick={openPositionDetails}
                              hasActiveFilters={hasActivePosFilters}
                              onResetFilters={resetPosFilters}
                            />
                          ) : sortedPositions.length === 0 ? (
                            <Card className="border-slate-200">
                              <CardContent className="p-10 text-center" data-testid="positions-list-empty">
                                <Filter className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                                <p className="text-sm text-slate-600 mb-3">No positions match your selected filter criteria</p>
                                <Button variant="outline" size="sm" onClick={resetPosFilters} data-testid="list-reset-filters-btn">
                                  Reset Filters
                                </Button>
                              </CardContent>
                            </Card>
                          ) : (
                            sortedPositions.map((position) => (
                              <PositionCard
                                key={position.position_id}
                                position={position}
                                schoolTOCs={schoolTOCs}
                                onEdit={handleOpenEditPosition}
                                onRemind={handleSendReminders}
                                onCancel={handleCancelPositionDirect}
                                onArchive={handleArchivePosition}
                                onViewDeclined={(pos) => {
                                  setDeclinedPosition(pos);
                                  setShowDeclinedDialog(true);
                                }}
                                onMessageSub={openMessageDialog}
                                messagingDisabled={!planHasFeature(selectedSchool?.plan_id, 'in_app_messaging')}
                                messagingDisabledMessage={upgradeTooltipMessage('in_app_messaging')}
                              />
                            ))
                          )}
                        </>
                      )}
                    </TabsContent>
                    
                    <TabsContent value="tocs" className="space-y-4">
                      {schoolTOCs.length === 0 ? (
                        <Card className="border-slate-200">
                          <CardContent className="p-12 text-center">
                            <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Subs connected yet</h3>
                            <p className="text-sm text-slate-500 mb-4">
                              Share your school code <span className="font-mono font-bold">{selectedSchool.school_code}</span> with substitute teachers
                            </p>
                            <Button 
                              variant="outline"
                              onClick={() => handleCopyCode(selectedSchool.school_code)}
                            >
                              <Copy className="h-4 w-4 mr-2" />
                              Copy Code
                            </Button>
                          </CardContent>
                        </Card>
                      ) : (
                        <div className="grid gap-4">
                          {/* Sort & Filter Controls */}
                          <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-lg p-3">
                            <div className="flex items-center gap-2">
                              <ArrowUpDown className="h-4 w-4 text-slate-400" />
                              <Select value={subListSortField} onValueChange={setSubListSortField}>
                                <SelectTrigger className="h-9 w-[190px] text-sm" data-testid="sub-sort-field">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="name">Sub Name</SelectItem>
                                  <SelectItem value="recent">Most Recent Position</SelectItem>
                                  <SelectItem value="added">Added Date</SelectItem>
                                </SelectContent>
                              </Select>
                              <Select value={subListSortDir} onValueChange={setSubListSortDir}>
                                <SelectTrigger className="h-9 w-[140px] text-sm" data-testid="sub-sort-dir">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="asc">Ascending</SelectItem>
                                  <SelectItem value="desc">Descending</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {activeSubCategories.length > 0 && (
                              <div className="flex items-center gap-2 ml-auto">
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="h-9 text-sm"
                                      data-testid="sub-category-filter-trigger"
                                    >
                                      <Filter className="h-3.5 w-3.5 mr-1.5" />
                                      {subListCategoryFilter.length === 0
                                        ? 'Filter by Category'
                                        : `${subListCategoryFilter.length} selected`}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-64 p-2" align="end">
                                    <div className="max-h-56 overflow-y-auto space-y-1">
                                      {activeSubCategories.map((cat) => (
                                        <label
                                          key={cat}
                                          className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm"
                                        >
                                          <Checkbox
                                            checked={subListCategoryFilter.includes(cat)}
                                            onCheckedChange={(checked) => {
                                              setSubListCategoryFilter(prev =>
                                                checked ? [...prev, cat] : prev.filter(c => c !== cat)
                                              );
                                            }}
                                            data-testid={`sub-category-filter-${cat}`}
                                          />
                                          <span>{cat}</span>
                                        </label>
                                      ))}
                                    </div>
                                    {subListCategoryFilter.length > 0 && (
                                      <button
                                        type="button"
                                        className="mt-2 w-full text-xs text-slate-500 hover:text-slate-700 text-center"
                                        onClick={() => setSubListCategoryFilter([])}
                                        data-testid="sub-category-filter-clear"
                                      >
                                        Clear filter
                                      </button>
                                    )}
                                  </PopoverContent>
                                </Popover>
                              </div>
                            )}
                          </div>
                          {displayedSubs.length === 0 ? (
                            <Card className="border-slate-200">
                              <CardContent className="p-8 text-center text-sm text-slate-500" data-testid="sub-list-no-match">
                                No Subs match the selected categories.
                              </CardContent>
                            </Card>
                          ) : displayedSubs.map((toc) => (
                            <Card key={toc.user_id} className={`border-slate-200 ${toc.status === 'blocked' ? 'opacity-60' : ''}`}>
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start gap-3">
                                    <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                                      {toc.picture ? (
                                        <img src={toc.picture} alt={toc.name} className="h-12 w-12 object-cover" />
                                      ) : (
                                        <User className="h-6 w-6 text-slate-600" />
                                      )}
                                    </div>
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <p className="font-medium text-slate-900">{toc.name}</p>
                                        {toc.status === 'blocked' && (
                                          <Badge variant="destructive" className="text-xs">Blocked</Badge>
                                        )}
                                      </div>
                                      <p className="text-sm text-slate-500">{toc.email}</p>
                                      {toc.phone && (
                                        <p className="text-sm text-slate-600 flex items-center gap-1">
                                          <span className="text-slate-400">Phone:</span> {toc.phone}
                                        </p>
                                      )}
                                      {toc.subjects && (
                                        <p className="text-sm text-slate-600">
                                          <span className="text-slate-400">Subjects:</span> {toc.subjects}
                                        </p>
                                      )}
                                      {toc.availability && (
                                        <p className="text-sm text-slate-600">
                                          <span className="text-slate-400">Availability:</span> {toc.availability}
                                        </p>
                                      )}
                                      {toc.stats && (
                                        <div className="flex gap-3 mt-2 text-xs">
                                          <span className="text-emerald-600">{toc.stats.filled || 0} filled</span>
                                          <span className="text-blue-600">{toc.stats.accepted || 0} accepted</span>
                                          <span className="text-slate-500">{toc.stats.acceptance_rate || 0}% rate</span>
                                        </div>
                                      )}
                                      <p className="text-sm text-slate-600 mt-1" data-testid={`most-recent-${toc.user_id}`}>
                                        <span className="text-slate-400">Most recent position:</span> {formatRecentDate(toc.most_recent_position)}
                                      </p>
                                      {/* Sub Teacher Categories */}
                                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                        {(toc.sub_categories || []).map((cat) => (
                                          <Badge key={cat} variant="secondary" className="text-xs" data-testid={`sub-cat-badge-${toc.user_id}-${cat}`}>
                                            {cat}
                                          </Badge>
                                        ))}
                                        {activeSubCategories.length > 0 ? (
                                          <Popover>
                                            <PopoverTrigger asChild>
                                              <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="h-6 px-2 text-xs"
                                                disabled={savingCategoriesFor === toc.user_id}
                                                data-testid={`assign-categories-${toc.user_id}`}
                                              >
                                                <Tags className="h-3 w-3 mr-1" />
                                                Categories
                                                <ChevronDown className="h-3 w-3 ml-1 opacity-60" />
                                              </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-56 p-2" align="start">
                                              <p className="text-xs font-medium text-slate-500 px-1 pb-1">Assign categories</p>
                                              <div className="max-h-56 overflow-y-auto space-y-1">
                                                {activeSubCategories.map((cat) => (
                                                  <label
                                                    key={cat}
                                                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm"
                                                  >
                                                    <Checkbox
                                                      checked={(toc.sub_categories || []).includes(cat)}
                                                      onCheckedChange={() => handleToggleSubCategory(toc.user_id, cat)}
                                                      data-testid={`assign-cat-${toc.user_id}-${cat}`}
                                                    />
                                                    <span>{cat}</span>
                                                  </label>
                                                ))}
                                              </div>
                                            </PopoverContent>
                                          </Popover>
                                        ) : (
                                          (toc.sub_categories || []).length === 0 && (
                                            <span className="text-xs text-slate-400 italic">
                                              No Sub Teacher Categories defined for this school yet
                                            </span>
                                          )
                                        )}
                                      </div>
                                      {/* Action Buttons */}
                                      <div className="mt-3 pt-2 border-t border-slate-100 flex flex-wrap gap-2">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="text-xs"
                                          onClick={() => handleViewUnavailability(toc)}
                                          disabled={loadingUnavailability}
                                          data-testid={`view-unavailability-${toc.user_id}`}
                                        >
                                          <CalendarDays className="h-3 w-3 mr-1" />
                                          Unavailability
                                        </Button>
                                        <PlanLimitTooltip
                                          variant="outline"
                                          size="sm"
                                          className="text-xs"
                                          onClick={() => openMessageDialog(selectedSchool.school_id, toc.user_id, toc.name)}
                                          data-testid={`message-toc-${toc.user_id}`}
                                          disabled={!planHasFeature(selectedSchool?.plan_id, 'in_app_messaging')}
                                          message={upgradeTooltipMessage('in_app_messaging')}
                                        >
                                          <MessageSquare className="h-3 w-3 mr-1" />
                                          Message
                                        </PlanLimitTooltip>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="text-xs text-amber-600 border-amber-200 hover:bg-amber-50"
                                          onClick={() => handleArchiveTOC(toc.user_id)}
                                          disabled={isArchiving}
                                          data-testid={`archive-toc-${toc.user_id}`}
                                        >
                                          <Archive className="h-3 w-3 mr-1" />
                                          Archive
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </TabsContent>
                    
                    {/* Archived Subs Tab */}
                    <TabsContent value="archived" className="space-y-4">
                      {archivedTOCs.length === 0 ? (
                        <Card className="border-slate-200">
                          <CardContent className="p-12 text-center">
                            <Archive className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-slate-700 mb-2">No archived Subs</h3>
                            <p className="text-sm text-slate-500">
                              Subs you archive will appear here. You can restore them at any time.
                            </p>
                          </CardContent>
                        </Card>
                      ) : (
                        <div className="grid gap-4">
                          {archivedTOCs.map((toc) => (
                            <Card key={toc.user_id} className="border-slate-200 bg-slate-50/50">
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start gap-3">
                                    <div className="h-12 w-12 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                                      {toc.picture ? (
                                        <img src={toc.picture} alt={toc.name} className="h-12 w-12 object-cover grayscale" />
                                      ) : (
                                        <User className="h-6 w-6 text-slate-400" />
                                      )}
                                    </div>
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <p className="font-medium text-slate-700">{toc.name}</p>
                                        <Badge variant="secondary" className="text-xs bg-slate-200 text-slate-600">Archived</Badge>
                                      </div>
                                      <p className="text-sm text-slate-500">{toc.email}</p>
                                      {toc.phone && (
                                        <p className="text-sm text-slate-500">Phone: {toc.phone}</p>
                                      )}
                                      {toc.stats && (
                                        <div className="flex gap-3 mt-2 text-xs text-slate-500">
                                          <span>{toc.stats.filled || 0} filled</span>
                                          <span>{toc.stats.accepted || 0} accepted</span>
                                          <span>{toc.stats.acceptance_rate || 0}% rate</span>
                                        </div>
                                      )}
                                      <div className="mt-3 pt-2 border-t border-slate-200">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                          onClick={() => handleUnarchiveTOC(toc.user_id)}
                                          disabled={isArchiving}
                                          data-testid={`unarchive-toc-${toc.user_id}`}
                                        >
                                          <ArchiveRestore className="h-3 w-3 mr-1" />
                                          Restore to Active
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </>
              )}
            </div>
          </div>
        )}
      </main>
      <AppFooter role="employer" />
      
      {/* Extracted Dialog Components */}
      <DeclinedSubsDialog
        open={showDeclinedDialog}
        onOpenChange={setShowDeclinedDialog}
        position={declinedPosition}
        schoolTOCs={schoolTOCs}
      />
      
      <UnavailabilityDialog
        open={showUnavailabilityDialog}
        onOpenChange={setShowUnavailabilityDialog}
        tocData={selectedTocUnavailability}
      />
      
      <MessageDialog
        open={showMessageDialog}
        onOpenChange={(open) => {
          setShowMessageDialog(open);
          if (!open) {
            setConversation([]);
            setMessageText('');
            setSelectedTocForMessage(null);
            setSelectedSchoolForMessage(null);
          }
        }}
        tocName={selectedTocForMessage?.name}
        conversation={conversation}
        messageText={messageText}
        setMessageText={setMessageText}
        onSendMessage={handleSendMessage}
        sendDisabled={!planHasFeature(
          schoolsList.find(s => s.school_id === selectedSchoolForMessage)?.plan_id,
          'in_app_messaging'
        )}
        showUpgradePrompt={showMessagingUpgrade}
        onUpgradePromptChange={setShowMessagingUpgrade}
        onDeleteMessage={async (msgId) => {
          try {
            await messages.deleteMessage(msgId);
            setConversation(prev => prev.filter(m => m.message_id !== msgId));
          } catch (_e) { /* Message may already be deleted */ }
        }}
        isSending={isSendingMessage}
        userId={user?.user_id}
      />
      
      <ArchivedSchoolsDialog
        open={showArchivedSchools}
        onOpenChange={setShowArchivedSchools}
        schools={archivedSchoolsList}
        onRestore={handleRestoreSchool}
      />
      
      <ArchivedPositionsDialog
        open={showArchivedPositions}
        onOpenChange={setShowArchivedPositions}
        positions={archivedPositionsList}
        loading={loadingArchivedPositions}
        onDelete={handlePermanentDeletePosition}
      />
      
      {/* Edit Position Dialog */}
      <EditPositionDialog
        open={showEditPosition}
        onOpenChange={setShowEditPosition}
        editingPosition={editingPosition}
        form={editPositionForm}
        setForm={setEditPositionForm}
        schoolTOCs={schoolTOCs}
        subBookings={subBookings}
        isUpdating={isUpdatingPosition}
        onUpdate={handleUpdatePosition}
        onCancel={handleCancelPosition}
      />

      <PositionDetailsDialog
        position={detailsPosition}
        open={showPositionDetails}
        onOpenChange={setShowPositionDetails}
        onEdit={handleOpenEditPosition}
      />
    </div>
  );
}
